/**
 * Page de connexion
 *
 * Rôle : Formulaire email/mot de passe ; appelle signIn() NextAuth (Credentials). Redirige vers callbackUrl ou /dashboard.
 * Gère superadmin (redirection vers /admin). Lien vers inscription et mot de passe oublié.
 * Technologies : React, next-auth (signIn, useSession), Next.js (useRouter)
 */
"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { getSession, signIn, useSession } from "next-auth/react";
import { Eye, EyeOff } from "lucide-react";
import { isSuperAdminEmail } from "@/lib/auth/constants";

export default function SignInPage() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Si déjà connecté, on redirige selon le rôle
  useEffect(() => {
    if (status === "authenticated") {
      const params = new URLSearchParams(window.location.search);
      const cb = params.get("callbackUrl") || "/dashboard";
      const isSuperAdmin =
        Boolean(session?.user?.is_superadmin) || isSuperAdminEmail(session?.user?.email);
      router.replace(isSuperAdmin ? "/admin/dashboard" : cb);
    }
  }, [status, session, router]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError("Identifiants invalides");
      } else if (result?.ok) {
        // Vérifier le rôle pour rediriger vers le bon dashboard
        const params = new URLSearchParams(window.location.search);
        const callbackUrl = params.get("callbackUrl");
        const nextSession = await getSession();
        const isSuperAdmin =
          Boolean(nextSession?.user?.is_superadmin) || isSuperAdminEmail(nextSession?.user?.email);
        router.push(isSuperAdmin ? "/admin/dashboard" : callbackUrl || "/dashboard");
      }
    } catch {
      setError("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-white">
      {/* Contenu */}
      <div className="flex-1 px-6 py-20">
        <div className="mx-auto w-full max-w-md">
          <div className="rounded-3xl border border-gray-200 bg-white shadow-lg p-8">
            <div className="text-center mb-8">
              <Link href="/">
                <Image
                  src="/logos/otulaa-logo.png"
                  alt="Otulaa"
                  width={200}
                  height={50}
                  priority
                  className="h-auto mx-auto mb-6 hover:opacity-80 transition-opacity duration-200"
                />
              </Link>
              <h1 className="text-3xl font-extrabold tracking-tight mb-2 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Connexion</h1>
              <p className="text-gray-600">Connectez-vous à votre espace Otulaa</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-6">
              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-600">
                  {error}
                </div>
              )}
              
              <div>
                <label htmlFor="email" className="text-sm font-semibold text-gray-900">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-300"
                  placeholder="votre@email.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="password" className="text-sm font-semibold text-gray-900">Mot de passe</label>
                <div className="mt-2 relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-300"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/20 rounded-lg p-1"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded border-gray-300" />
                  Se souvenir de moi
                </label>
                <Link
                  href="/forgot-password"
                  className="text-sm text-gray-700 hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-gradient-to-r from-orange-400 to-red-500 text-white py-3 text-lg font-bold shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </button>
            </form>

            <div className="mt-8 text-center text-sm text-gray-700">
              Pas encore de compte ?{" "}
              <a href="/signup" className="text-orange-600 font-semibold hover:underline">Créer un compte</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
